'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["href", "title", "index", "children", "className", "classes", "slots", "slotProps"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useSlotProps from '@mui/utils/useSlotProps';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageSourceUtilityClasses } from "./chatMessageSourceClasses.mjs";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageSource');
const ChatMessageSourceRootStyled = styled('li', {
  name: 'MuiChatMessageSource',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  minWidth: 0
}));
const ChatMessageSourceIndexStyled = styled('span', {
  name: 'MuiChatMessageSource',
  slot: 'Index',
  overridesResolver: (_, styles) => styles.index
})(({
  theme
}) => ({
  fontSize: '0.65rem',
  color: (theme.vars || theme).palette.text.disabled,
  minWidth: 16,
  textAlign: 'center',
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.5
}));
const ChatMessageSourceLinkStyled = styled('a', {
  name: 'MuiChatMessageSource',
  slot: 'Link',
  overridesResolver: (_, styles) => styles.link
})(({
  theme
}) => ({
  color: (theme.vars || theme).palette.primary.main,
  fontSize: theme.typography.caption.fontSize,
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline'
  }
}));
const ChatMessageSource = /*#__PURE__*/React.forwardRef(function ChatMessageSource(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageSource'
  });
  const {
      href,
      title,
      index: indexProp,
      children,
      className,
      classes: classesProp,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatMessageSourceUtilityClasses(classesProp);
  const Root = slots?.root ?? ChatMessageSourceRootStyled;
  const Index = slots?.index ?? ChatMessageSourceIndexStyled;
  const LinkSlot = slots?.link ?? ChatMessageSourceLinkStyled;
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState: {},
    additionalProps: {
      ref,
      className: clsx(classes.root, className)
    }
  });
  const indexProps = useSlotProps({
    elementType: Index,
    externalSlotProps: slotProps?.index,
    ownerState: {},
    additionalProps: {
      className: classes.index
    }
  });
  const linkProps = useSlotProps({
    elementType: LinkSlot,
    externalSlotProps: slotProps?.link,
    ownerState: {},
    additionalProps: {
      href,
      target: '_blank',
      rel: 'noreferrer noopener',
      className: classes.link
    }
  });
  return /*#__PURE__*/_jsxs(Root, _extends({}, rootProps, {
    children: [indexProp != null && /*#__PURE__*/_jsxs(Index, _extends({}, indexProps, {
      children: ["[", indexProp, "]"]
    })), /*#__PURE__*/_jsx(LinkSlot, _extends({}, linkProps, {
      children: children ?? title ?? href
    }))]
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageSource.displayName = "ChatMessageSource";
process.env.NODE_ENV !== "production" ? ChatMessageSource.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * The URL this source links to.
   */
  href: PropTypes.string.isRequired,
  /**
   * 1-based position number displayed as a muted badge.
   */
  index: PropTypes.number,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  /**
   * Display title for the source. Falls back to `href` when omitted.
   */
  title: PropTypes.string
} : void 0;
export { ChatMessageSource };